/**
 * Typed fetch wrapper for the loremap REST surface (protocol/api.md v2).
 *
 * Every non-2xx JSON response carries the error envelope
 * `{ error: { code, message } }` — surfaced here as `ApiClientError` so
 * callers can branch on the stable `code` registry (api.md §1).
 *
 * v2 auth: every protected route 401s with `auth_required` when the session
 * cookie is missing/expired. The app registers a handler
 * (`setAuthRequiredHandler`) that redirects to /login with a return path;
 * `api()` notifies it on every 401 before throwing, so an expired session
 * mid-use lands on the login screen instead of a dead error state.
 */

import type { ApiError, RepoDetail } from './types';

export const API_BASE = '/api';

export class ApiClientError extends Error {
  /** stable machine code from the registry in protocol/api.md §1 */
  readonly code: string;
  /** HTTP status */
  readonly status: number;

  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.status = status;
  }
}

function isApiError(body: unknown): body is ApiError {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as ApiError).error === 'object' &&
    (body as ApiError).error !== null &&
    typeof (body as ApiError).error.code === 'string' &&
    typeof (body as ApiError).error.message === 'string'
  );
}

/** Parse a non-OK response into an ApiClientError (never throws while parsing). */
export async function errorFromResponse(res: Response): Promise<ApiClientError> {
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    // non-JSON error body (proxy hiccup etc.)
  }
  if (isApiError(body)) {
    return new ApiClientError(body.error.code, body.error.message, res.status);
  }
  return new ApiClientError('internal', `HTTP ${res.status}`, res.status);
}

// ---------------------------------------------------------------------------
// 401 → /login redirect hook (api.md §1 `auth_required`)
// ---------------------------------------------------------------------------

export type AuthRequiredHandler = (err: ApiClientError) => void;

let authRequiredHandler: AuthRequiredHandler | null = null;

/**
 * Register the app-level 401 handler (the route shell wires this to
 * `navigate('/login?next=…')`). Returns the unregister function. The auth
 * probe itself (`GET /api/auth/session`) never 401s by contract, so the
 * handler only fires for real protected-route failures.
 */
export function setAuthRequiredHandler(handler: AuthRequiredHandler | null): () => void {
  authRequiredHandler = handler;
  return () => {
    if (authRequiredHandler === handler) authRequiredHandler = null;
  };
}

/** Notify the registered handler about a 401 (no-op when none registered). */
export function notifyAuthRequired(err: ApiClientError): void {
  if (err.status === 401 && err.code === 'auth_required') {
    authRequiredHandler?.(err);
  }
}

export interface ApiRequestInit {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

/**
 * Core request. JSON in, JSON out; `undefined` for 204/empty bodies.
 * Artifact endpoints ride the browser HTTP cache: the server serves strong
 * ETags and we pass `If-None-Match` implicitly via default cache mode.
 * The session cookie rides along automatically (same-origin).
 */
export async function api<T>(path: string, init: ApiRequestInit = {}): Promise<T> {
  const { method = 'GET', body, signal, headers = {} } = init;
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    signal: signal ?? null,
    headers: {
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body: body !== undefined ? JSON.stringify(body) : null,
  });
  if (!res.ok) {
    const err = await errorFromResponse(res);
    notifyAuthRequired(err);
    throw err;
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const text = await res.text();
  if (text.length === 0) {
    return undefined as T;
  }
  return JSON.parse(text) as T;
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return api<T>(path, { signal });
}

export function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return api<T>(path, { method: 'POST', body, signal });
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, { method: 'PUT', body });
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return api<T>(path, { method: 'PATCH', body });
}

export function apiDelete<T = void>(path: string): Promise<T> {
  return api<T>(path, { method: 'DELETE' });
}

export interface UploadProgress {
  loaded: number;
  total: number | null;
}

/**
 * `POST /api/products/{pid}/repos/upload` — multipart zip upload with
 * progress. fetch has no upload progress; XHR does, so this one endpoint
 * uses XHR.
 */
export function uploadRepoZip(
  productId: string,
  name: string,
  file: File | Blob,
  onProgress?: (p: UploadProgress) => void,
): Promise<RepoDetail> {
  return new Promise((resolve, reject) => {
    const form = new FormData();
    form.append('name', name);
    form.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE}/products/${productId}/repos/upload`);
    xhr.responseType = 'text';
    if (xhr.upload && onProgress) {
      xhr.upload.onprogress = (e) => {
        onProgress({ loaded: e.loaded, total: e.lengthComputable ? e.total : null });
      };
    }
    xhr.onerror = () => reject(new ApiClientError('internal', 'network error during upload', 0));
    xhr.onload = () => {
      let body: unknown = null;
      try {
        body = JSON.parse(xhr.responseText) as unknown;
      } catch {
        // fall through — handled below
      }
      if (xhr.status >= 200 && xhr.status < 300 && body !== null) {
        resolve(body as RepoDetail);
        return;
      }
      if (isApiError(body)) {
        const err = new ApiClientError(body.error.code, body.error.message, xhr.status);
        notifyAuthRequired(err);
        reject(err);
        return;
      }
      reject(new ApiClientError('internal', `HTTP ${xhr.status}`, xhr.status));
    };
    xhr.send(form);
  });
}

/**
 * Encode an entity id for use in a URL path segment. The server matches
 * greedily so raw slashes work, but percent-encoding each segment is also
 * accepted (api.md §1) and keeps us safe in browser URL normalization.
 * `:` in infra ids (svc:web) needs no encoding.
 */
export function encodeEntityIdForPath(entityId: string): string {
  return entityId.split('/').map(encodeURIComponent).join('/');
}
